/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { JSX } from "react";

import { Locale, LocaleKeys } from "../../../model/locale";
import { TMyHubActionType } from "../../../viewModel/myHub/myHubActionTypes";
// Imported from the ContentCard folder rather than the `views/common` barrel:
// that barrel also re-exports connected containers, so pulling it in here would
// drag the whole viewModel graph into this leaf module and into its tests.
import {
  DeleteIcon,
  ExtendIcon,
  InfoIcon,
  PermanentlyDeleteIcon,
  PlayIcon,
  SaveIcon,
  StopIcon,
  UndeleteIcon,
  UnsaveIcon
} from "../../common/ContentCard";

/** Class the popup sizes its action glyphs with. */
const ICON_CLASS: string = "my-hub-recording-highlight__action-icon";

/** One action button, resolved for render. */
export type TRecordingHighlightAction = {
  id: TMyHubActionType;
  label: string;
  icon: JSX.Element;
};

/**
 * Icon and locale key per action the popup can render.
 *
 * Keyed by the service's own {@link TMyHubActionType} rather than a set of
 * button ids owned by this surface: the press is ultimately handed to
 * `performMyHubAction`, which speaks that type, so a private id space would only
 * add a projection table between the two.
 *
 * `Partial` only because the enum may outgrow this table, not because any
 * action is currently missing: every type the popup can be handed now has a
 * glyph, EXTEND included (`ic_arrow_top_right`). `toRecordingHighlightItem`
 * still drops anything without a spec before the item is stored, so nothing
 * reaching here can lack one.
 *
 * Labels are resolved at call time rather than baked in, so a locale change is
 * picked up — the same reason `savedActionCatalog` keeps its keys.
 */
const HIGHLIGHT_ACTION_SPECS: Partial<
  Record<TMyHubActionType, { localeKey: string; icon: JSX.Element }>
> = {
  [TMyHubActionType.PLAY]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_PLAY,
    icon: <PlayIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.DELETE]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_DELETE,
    icon: <DeleteIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.SAVE]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_SAVE,
    icon: <SaveIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.UNSAVE]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_UNSAVE,
    icon: <UnsaveIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.STOP]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_STOP,
    icon: <StopIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.UNDELETE]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_UNDELETE,
    icon: <UndeleteIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.PERMANENTLY_DELETE]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_PERMANENTLY_DELETE,
    icon: <PermanentlyDeleteIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.DETAILS]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_DETAILS,
    icon: <InfoIcon className={ICON_CLASS} />
  },
  [TMyHubActionType.EXTEND]: {
    localeKey: LocaleKeys.CONTENT_CARD_ACTION_EXTEND,
    icon: <ExtendIcon className={ICON_CLASS} />
  }
};

/**
 * Maps an item's action types onto buttons, in the service's order.
 *
 * An action this table has no spec for is dropped rather than asserted away:
 * the service owns {@link TMyHubActionType} and can start sending a type this
 * surface has no glyph for, which would otherwise read `spec.localeKey` off
 * `undefined` and take the whole popup down. Dropping it costs one button and
 * leaves the rest usable.
 *
 * The popup's focus index walks the same stored `availableActions` this reads,
 * so a dropped action would shift the cursor off by one. That is why nothing is
 * expected to drop in practice — every type the service sends has a spec above
 * — and why this is a safety net rather than a filter the design relies on.
 *
 * @param actionTypes - The item's stored `availableActions`.
 * @returns Render-ready actions, in button order.
 */
export const toRecordingHighlightActions = (
  actionTypes: readonly TMyHubActionType[]
): TRecordingHighlightAction[] => {
  const actions: TRecordingHighlightAction[] = [];

  actionTypes.forEach((actionType: TMyHubActionType): void => {
    const spec = HIGHLIGHT_ACTION_SPECS[actionType];
    if (spec === undefined) {
      return;
    }
    actions.push({
      id: actionType,
      label: Locale.getString(spec.localeKey),
      icon: spec.icon
    });
  });

  return actions;
};
